// Everything painted ON TOP of the felt: the highlight, the placed chips, and
// the winner reveal.
//
// ONE mechanism serves three jobs, because they must never fight each other:
//   preview  — while a finger is down on a bet (live, follows the finger)
//   confirm  — 1.4s flash after placing, so the player is sure what they bought
//   winner   — 3.5s hold on the result, so they can see what came and whether
//              they won before the next round starts
// They are ranked (winner > preview > confirm) in useHighlight, so a stuck
// preview can never hide the result.
//
// Distinguishable WITHOUT colour: solid ring + corner brackets (preview),
// marching dashes + hatch (confirm), double ring + heavy scrim (winner).
import { cellRect, pitchX, pitchY, type Cell, type Lattice } from './lattice';
import { GAP } from './Board';

export type HlReason = 'preview' | 'confirm' | 'winner';

export type Highlight = {
  reason: HlReason;
  cells: Cell[];
  marker?: { x: number; y: number } | null;
  seq: number;
};

export type PlacedChip = {
  id: string;
  amount: number;
  fx: number;          // marker in PITCH units — resolution independent, so a
  fy: number;          // resize or rotate never strands a chip off its bet
  /** true when the bet is a straight-up on a cell CENTRE — the one case where a
   *  centred chip would sit on top of the number. Those get nudged into the
   *  cell's bottom-right corner instead. */
  centre?: boolean;
  won?: boolean;
  /**
   * SHARED MODE ONLY (Phase E, plan §5.7, 2026-09-06). Undefined on the local path, which is
   * why nothing about the shipped game changes: the attribute is simply absent and the chip
   * paints exactly as it always has.
   *
   *   'pending'  — posted, no ack yet. Translucent: the money is spoken for but the table has
   *                not confirmed it, and a solid chip is a promise the server has not made.
   *   'placed'   — acked. Solid, i.e. the shipped look.
   *   'unplaced' — refused, or still un-acked when the window shut. Greyed, and the strip says
   *                NOT PLACED — KEPT FOR NEXT SPIN. The rupees are back in the wallet.
   */
  ack?: 'pending' | 'placed' | 'unplaced';
};

const money = (v: number) => (v >= 1000 ? `${+(v / 1000).toFixed(1)}k` : String(v));

export default function Overlay({
  lattice, highlight, chips, dimOthers, points, railL,
}: {
  lattice: Lattice | null;
  /** Measured rail width in px. NOT a constant any more — the board is five
   *  equal columns, so the rail is one fifth of whatever the grid measures. */
  railL: number;
  highlight: Highlight | null;
  chips: PlacedChip[];
  /** true while a round is running — the felt sits back so the wheel owns the eye */
  dimOthers: boolean;
  /** Every place a chip can go on a LINE. Non-null only while a token is in
   *  hand, so the board is clean until the player has something to place —
   *  and then it shows them, rather than expecting them to know that roulette
   *  has splits, corners and six lines hidden on the gridlines. */
  points: { x: number; y: number; k: 'v' | 'h' | 'p' }[] | null;
}) {
  if (!lattice) return null;
  const L = lattice;
  const PX = pitchX(L), PY = pitchY(L);
  const W = 3 * L.cellW + 2 * L.gap;
  const H = 13 * PY - L.gap;                 // rows 0..12; the 2:1 strip is below
  // The overlay's coordinate space starts at number column 0 — i.e. after the
  // The overlay's coordinate space starts at number column 0 — after the LEFT
  // rail. Measured, because all five columns are equal and the rail therefore
  // scales with the board.
  const originX = railL + GAP;

  const rects = highlight ? highlight.cells.map((c) => cellRect(L, c)) : [];
  const wide = rects.length > 6;             // outside / dozen / column
  const reason = highlight?.reason;

  const ring =
    reason === 'winner' ? '#ffe103'
      : reason === 'confirm' ? '#ffd54a'
        : '#ffffff';

  return (
    <div className="pointer-events-none absolute inset-0 z-[2]" aria-hidden>
      <div className="absolute" style={{ left: originX, top: 0, width: W, height: H }}>
        <svg
          width={W} height={H} viewBox={`0 0 ${W} ${H}`}
          style={{ overflow: 'visible', display: 'block' }}
        >
          <defs>
            <mask id="rlt-hole">
              <rect width={W} height={H} fill="#fff" />
              {rects.map((q, i) => <rect key={i} {...q} rx={4} fill="#000" />)}
            </mask>
            <pattern id="rlt-hatch" width="8" height="8" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
              <rect width="3" height="8" fill="#ffd54a" opacity=".3" />
            </pattern>
          </defs>

          {/* Scrim. A scattered set like RED is unreadable as 18 separate rings;
              dimming everything ELSE is what makes coverage unmistakable.
              PREVIEW AND CONFIRM ONLY — never the winner. MEASURED: in the
              reference, the unlit board during the RESULT is byte-identical to the
              board during betting. Cell "2" (x266-310, y400-445) reads
              (39.6,87.7,81.0) in ref1, ref5 AND ref6; an 8x8 block diff of ref5
              against ref6 across the whole board is flat except inside the six
              washed zones. Under rgba(2,10,6,.72) that cell would read (13,32,27).
              This scrim was firing for the entire 7000ms result hold and blacking
              out the whole grid — the single largest departure from the reference
              in the result phase. .rlt-won-cell::after already carries the whole
              effect the reference does have. */}
          {highlight && wide && reason !== 'winner' && (
            <rect
              width={W} height={H} mask="url(#rlt-hole)"
              fill="rgba(2,10,6,.60)"
              className="rlt-fade"
            />
          )}

          {highlight && rects.map((q, i) => (
            <g key={`${highlight.seq}-${i}`} className={`rlt-hl rlt-hl--${reason}`} style={{ animationDelay: `${i * 16}ms` }}>
              {reason === 'confirm' && <rect {...q} rx={4} fill="url(#rlt-hatch)" />}
              {reason === 'preview' && <rect {...q} rx={4} fill="rgba(255,255,255,.14)" />}
              {/* NO RING ON THE WINNER. MEASURED: scanning every edge of the
                  winning cell in ref5, the brightest pixel is (181,230,117) —
                  which is exactly the board's own white gridline composited under
                  the 50% yellow wash. #ffe103 = (255,225,3) appears at no pixel on
                  any cell boundary in ref5 or ref6, and a 4px stroke centred on the
                  edge would also paint 2px OUTSIDE the cell, which is likewise
                  absent. A control scan of an unlit disc edge shows no overshoot,
                  so this is not JPEG ringing. */}
              {reason !== 'winner' && (
                <rect
                  {...q} rx={4} fill="none" stroke={ring}
                  strokeWidth={2.5}
                  strokeDasharray={reason === 'confirm' ? '6 4' : undefined}
                />
              )}
              {/* corner brackets — the non-colour tell that these cells are ONE bet */}
              {reason === 'preview' && !wide && (
                <path
                  d={`M${q.x + 2},${q.y + 11} v-9 h9 M${q.x + q.width - 11},${q.y + 2} h9 v9
                      M${q.x + q.width - 2},${q.y + q.height - 11} v9 h-9
                      M${q.x + 11},${q.y + q.height - 2} h-9 v-9`}
                  fill="none" stroke={ring} strokeWidth={3} strokeLinecap="round"
                />
              )}
            </g>
          ))}

          {/* the placeable points — studs on the gridlines */}
          {points && points.map((p, i) => (
            <circle key={`pt${i}`} cx={p.x} cy={p.y} r={p.k === 'p' ? 3.4 : 2.6}
                    className="rlt-pt" style={{ animationDelay: `${(i % 12) * 22}ms` }} />
          ))}

          {/* the exact spot the chip would land — this is what turns a 4mm
              corner from a guess into an aim */}
          {highlight?.marker && (
            <g className="rlt-marker">
              <circle cx={highlight.marker.x} cy={highlight.marker.y} r={12} fill="none" stroke="#0b0f0c" strokeWidth={5} />
              <circle cx={highlight.marker.x} cy={highlight.marker.y} r={12} fill="none" stroke="#fff" strokeWidth={2.5} />
            </g>
          )}
        </svg>

        {/* Placed chips. Same object as the tray tokens — a real chip face with
            the stake printed INSIDE it, so what you picked up is what you see on
            the felt. A straight-up chip is nudged into the cell's bottom-right
            so it never sits on top of the number. */}
        {chips.map((c) => {
          const nudgeX = c.centre ? L.cellW / 2 - 17 : 0;
          const nudgeY = c.centre ? L.cellH / 2 - 14 : 0;
          return (
            <div
              key={c.id}
              className={`rlt-coin-wrap ${c.won ? 'rlt-chip-won' : ''} ${dimOthers && !c.won ? 'opacity-60' : ''}`}
              // Absent on the local path — see `PlacedChip.ack`. A data attribute rather than a
              // class so the three states are mutually exclusive by construction and CSS can
              // never composite two of them onto one chip.
              data-ack={c.ack}
              // Clamped inside the surface: a street or six-line marker sits half
              // a gap OUTSIDE the number columns — correct on a real felt, but
              // the board's right edge is 8px from the screen edge, so the chip
              // was being sliced in half by the viewport.
              style={{
                // A street or six-line marker sits ON the right border of the
                // last number column, which is now the dozen rail rather than
                // the screen edge — so it may overhang a little, exactly as a
                // chip does on a real felt. Still clamped, just less harshly.
                left: Math.max(15, Math.min(W + Math.min(railL, 20), c.fx * PX + nudgeX)),
                top: Math.max(15, c.fy * PY + nudgeY),
              }}
            >
              <span className="rlt-coin" data-v={String(c.amount)}>
                <span className="rlt-coin-face">{money(c.amount)}</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
